import { type HTMLAttributes } from 'react';

export type SkeletonProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Tailwind size classes. Defaults to `h-4 w-full`.
   *
   * @example
   *   <Skeleton shape="h-6 w-32" />
   *   <Skeleton shape="h-40 w-full rounded-card" />
   */
  shape?: string;
  /**
   * Override the polite "Cargando…" SR label. Pass a domain-specific phrase
   * when the parent already announces the section.
   */
  srLabel?: string;
  /**
   * Inline style passthrough (rare — prefer Tailwind classes). Useful when
   * the size matches a sibling element with fixed pixel dimensions
   * (e.g., reserving room for a QR code).
   */
  style?: HTMLAttributes<HTMLDivElement>['style'];
};

/**
 * Loading placeholder. Tailwind's `animate-pulse` provides the shimmer.
 *
 * Renders an `aria-live="polite"` region — screen readers announce "Cargando…"
 * once per mount. Don't pile a Skeleton per list item with announcements
 * (override `srLabel=""` for sub-items to silence them; the parent already
 * announces).
 *
 * @example
 *   {isLoading ? <Skeleton shape="h-32 w-full" /> : <EventCard event={data} />}
 */
export function Skeleton({
  shape = 'h-4 w-full',
  srLabel = 'Cargando…',
  className = '',
  ...rest
}: SkeletonProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={srLabel || undefined}
      aria-busy
      className={`animate-pulse rounded-card bg-fc-line/60 ${shape} ${className}`.trim()}
      {...rest}
    />
  );
}

/** Group helper — collapses inner `srLabel` announcements to a single one. */
export function SkeletonGroup({
  label = 'Cargando…',
  className = '',
  children,
}: {
  label?: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div role="status" aria-live="polite" aria-label={label} aria-busy className={className}>
      {children}
    </div>
  );
}
